/**
 * UI Components - Moon Moon Agency  
 * Handles dropdowns, read more functionality, and loader states
 */

/*********************************************************************************************
 * 🚀 LOADER MODULE
 */
const LoaderManager = {
	// Local configuration
	config: {
		classes: {
			loading: 'mm-loading',
			active: 'mm-active',
			expanded: 'mm-expanded'
		},
		selectors: {
			dropdownTrigger: '[data-dropdown-trigger]',
			readMoreToggle: '[data-read-more-toggle]',
			readMoreContainer: '[data-read-more]',
			readMoreText: '[data-read-more-text]',
			readMoreLabelShow: '[data-read-more-show]',
			readMoreLabelHide: '[data-read-more-hide]'
		}
	},

	/**
	 * Add loading state to specified element
	 */
	loadThis(elem) {
		if (!elem) {
			window.log('warn', 'LoaderManager', 'No element provided');
			return;
		}
		elem.classList.add(this.config.classes.loading);
		window.emit('global:loader-activated', { element: elem.tagName });
	}
};

/*********************************************************************************************
 * 📋 DROPDOWN MODULE
 */
const DropdownManager = {
	/**
	 * Close all dropdowns
	 */
	closeAll(dropWrappers) {
		dropWrappers.forEach((el) => {
			el.classList.remove(LoaderManager.config.classes.active);
			el.setAttribute('aria-expanded', 'false');
			if (el.nextElementSibling) {
				el.nextElementSibling.style.maxHeight = null;
				el.nextElementSibling.setAttribute('inert', '');
			}
		});
	},

	/**
	 * Toggle dropdown
	 */
	toggle(trigger, dropWrappers) {
		// Close other dropdowns
		dropWrappers.forEach((el) => {
			if (el !== trigger) {
				el.classList.remove(LoaderManager.config.classes.active);
				el.setAttribute('aria-expanded', 'false');
				if (el.nextElementSibling) {
					el.nextElementSibling.style.maxHeight = null;
					el.nextElementSibling.setAttribute('inert', '');
				}
			}
		});

		// Toggle current dropdown
		trigger.classList.toggle(LoaderManager.config.classes.active);
		const content = trigger.nextElementSibling;

		if (content) {
			const isActive = trigger.classList.contains(LoaderManager.config.classes.active);
			trigger.setAttribute('aria-expanded', isActive.toString());
			content.style.maxHeight = isActive ? `${content.scrollHeight}px` : null;
			if (isActive) {
				content.removeAttribute('inert');
			} else {
				content.setAttribute('inert', '');
			}

			window.emit('global:dropdown-toggled', {
				active: isActive,
				element: trigger.tagName
			});
		}
	},

	/**
	 * Initialize dropdown behaviors
	 */
	init() {
		const dropWrappers = window.getElements(LoaderManager.config.selectors.dropdownTrigger);
		if (!dropWrappers.length) return;

		// Initialize aria-expanded + inert on closed content for all dropdowns
		dropWrappers.forEach((item) => {
			if (!item.hasAttribute('aria-expanded')) {
				item.setAttribute('aria-expanded', 'false');
			}

			const content = item.nextElementSibling;
			// Only mark content as inert if it is actually hidden via CSS
			// (otherwise desktop footer/nav content would become unfocusable).
			if (content && item.getAttribute('aria-expanded') !== 'true') {
				const isHidden = content.offsetHeight === 0 || getComputedStyle(content).maxHeight === '0px';
				if (isHidden) {
					content.setAttribute('inert', '');
				}
			}

			// Click event
			item.addEventListener('click', () => {
				this.toggle(item, dropWrappers);
			});

			// Keyboard support (Enter/Space)
			item.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.toggle(item, dropWrappers);
				}
			});
		});

		// Global Escape key handler
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				const hasActiveDropdown = Array.from(dropWrappers).some((el) =>
					el.classList.contains(LoaderManager.config.classes.active)
				);

				if (hasActiveDropdown) {
					this.closeAll(dropWrappers);
					// Return focus to active dropdown trigger
					const activeTrigger = Array.from(dropWrappers).find((el) =>
						el.classList.contains(LoaderManager.config.classes.active)
					);
					activeTrigger?.focus();
				}
			}
		});
	}
};

/*********************************************************************************************
 * 📖 READ MORE MODULE
 */
const ReadMoreManager = {
	/**
	 * Toggle read more state
	 */
	/**
	 * Cible deportee (data-read-more-target) : si elle est presente sur la page,
	 * le bouton y defile et la deplie au lieu de deplier le texte local
	 */
	getTarget(btn) {
		const selector = btn.dataset.readMoreTarget;
		return selector ? document.querySelector(selector) : null;
	},

	toggle(btn) {
		const target = this.getTarget(btn);
		if (target) {
			target.dispatchEvent(new CustomEvent('read-more:expand', { bubbles: true }));
			target.scrollIntoView({ block: 'start' });
			return;
		}

		const container = btn.closest(LoaderManager.config.selectors.readMoreContainer);
		if (!container) return;

		const textElement = container.querySelector(LoaderManager.config.selectors.readMoreText);
		const labelShow = btn.querySelector(LoaderManager.config.selectors.readMoreLabelShow);
		const labelHide = btn.querySelector(LoaderManager.config.selectors.readMoreLabelHide);

		if (!textElement || !labelShow || !labelHide) return;

		const expanded = btn.getAttribute('aria-expanded') === 'true';
		const newState = !expanded;

		// Update state
		textElement.classList.toggle(LoaderManager.config.classes.expanded, newState);
		btn.setAttribute('aria-expanded', newState.toString());
		labelShow.hidden = newState;
		labelHide.hidden = !newState;

		window.emit('global:read-more-toggled', { expanded: newState });
	},

	/**
	 * Show/hide toggle button based on whether the text overflows the clamp.
	 * If the text fits entirely in the visible area, no "read more" is needed.
	 */
	updateVisibility(btn) {
		const container = btn.closest(LoaderManager.config.selectors.readMoreContainer);
		if (!container) return;

		const textElement = container.querySelector(LoaderManager.config.selectors.readMoreText);
		if (!textElement) return;

		// Skip measurement when expanded (clamp removed → scrollHeight == clientHeight)
		if (btn.getAttribute('aria-expanded') === 'true') return;

		// Skip hidden containers (display:none → zero dimensions, unreliable measurement)
		if (container.offsetParent === null) return;

		const overflows = textElement.scrollHeight > textElement.clientHeight + 1;
		btn.hidden = !overflows;
	},

	/**
	 * Initialize read more functionality
	 */
	init() {
		const toggleButtons = window.getElements(LoaderManager.config.selectors.readMoreToggle);
		if (!toggleButtons.length) return;

		toggleButtons.forEach((btn) => {
			// Initialize aria-expanded if not present (sans objet si le bouton renvoie vers une cible deportee)
			if (this.getTarget(btn)) {
				btn.removeAttribute('aria-expanded');
			} else if (!btn.hasAttribute('aria-expanded')) {
				btn.setAttribute('aria-expanded', 'false');
			}

			this.updateVisibility(btn);

			// Click event
			btn.addEventListener('click', () => {
				this.toggle(btn);
			});

			// Keyboard support (Enter/Space)
			btn.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.toggle(btn);
				}
			});
		});

		const onResize = window.debounce(() => {
			toggleButtons.forEach((btn) => this.updateVisibility(btn));
		}, 150);
		window.addEventListener('resize', onResize);
	}
};

/*********************************************************************************************
 * ↔️ DRAG SCROLL MODULE
 * Conteneur scrollable horizontalement : glisser à la souris + flèche "aller à la fin / revenir au début".
 * Souris uniquement : le tactile garde le scroll natif, la flèche reste masquée.
 */
const DragScrollManager = {
	config: {
		selectors: {
			container: '[data-drag-scroll]',
			track: '[data-drag-scroll-track]',
			arrow: '[data-drag-scroll-arrow]'
		},
		classes: {
			draggable: 'is-draggable',
			dragging: 'is-dragging',
			end: 'is-end'
		},
		// Distance (px) au-delà de laquelle un clic devient un glisser
		dragThreshold: 5
	},

	/**
	 * Affiche la flèche si le contenu dépasse, et l'oriente selon la position du scroll
	 */
	_update(track, arrow) {
		const overflows = track.scrollWidth > track.clientWidth + 1;
		track.classList.toggle(this.config.classes.draggable, overflows);
		if (!arrow) return;

		arrow.hidden = !overflows;
		const isEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
		arrow.classList.toggle(this.config.classes.end, isEnd);
		arrow.setAttribute('aria-label', isEnd ? arrow.dataset.labelStart : arrow.dataset.labelEnd);
	},

	/**
	 * Glisser à la souris : un clic sur un lien reste un clic tant qu'on ne dépasse pas le seuil
	 */
	_bindDrag(track) {
		let startX = 0;
		let startScroll = 0;
		let isDown = false;
		let hasDragged = false;

		track.addEventListener('pointerdown', (e) => {
			if (e.pointerType !== 'mouse' || e.button !== 0) return;
			isDown = true;
			hasDragged = false;
			startX = e.clientX;
			startScroll = track.scrollLeft;
		});

		track.addEventListener('pointermove', (e) => {
			if (!isDown) return;
			const deltaX = e.clientX - startX;
			if (!hasDragged && Math.abs(deltaX) < this.config.dragThreshold) return;

			if (!hasDragged) {
				hasDragged = true;
				// Capture seulement une fois le glisser confirmé, sinon le clic sur le lien serait perdu
				track.setPointerCapture(e.pointerId);
				track.classList.add(this.config.classes.dragging);
			}
			track.scrollLeft = startScroll - deltaX;
		});

		const stopDrag = () => {
			isDown = false;
			track.classList.remove(this.config.classes.dragging);
		};
		track.addEventListener('pointerup', stopDrag);
		track.addEventListener('pointercancel', stopDrag);

		// Après un glisser, on bloque le clic qui suit pour ne pas suivre le lien
		track.addEventListener('click', (e) => {
			if (!hasDragged) return;
			e.preventDefault();
			e.stopPropagation();
			hasDragged = false;
		}, true);

		// Empêche le drag & drop natif des liens qui interromprait le glisser
		track.addEventListener('dragstart', (e) => e.preventDefault());
	},

	init() {
		const containers = window.getElements(this.config.selectors.container);
		if (!containers.length) return;
		if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

		containers.forEach((container) => {
			const track = container.querySelector(this.config.selectors.track);
			if (!track) return;
			const arrow = container.querySelector(this.config.selectors.arrow);

			this._bindDrag(track);

			if (arrow) {
				arrow.addEventListener('click', () => {
					const isEnd = arrow.classList.contains(this.config.classes.end);
					track.scrollTo({ left: isEnd ? 0 : track.scrollWidth, behavior: 'smooth' });
				});
			}

			track.addEventListener('scroll', window.debounce(() => this._update(track, arrow), 50), { passive: true });
			new ResizeObserver(() => this._update(track, arrow)).observe(track);
		});
	}
};

/*********************************************************************************************
 * 🔗 EXTERNAL LINKS MODULE
 */
const ExternalLinksManager = {
	/**
	 * Process external links to open in new tab
	 */
	init() {
		const links = window.getElements('a:not(.consent-wrapper)');
		let processedCount = 0;

		links.forEach((link) => {
			if (link.hostname !== window.location.hostname) {
				link.target = '_blank';
				link.rel = 'noreferrer noopener';
				processedCount++;
			}
		});

	}
};

/*********************************************************************************************
 * 📏 DOCUMENT HEIGHT MODULE
 */
const DocumentHeightManager = {
	/**
	 * Update document height CSS variable
	 */
	update() {
		const newHeight = `${window.innerHeight}px`;
		document.documentElement.style.setProperty('--doc-height', newHeight);
		window.emit('global:document-height-updated', { height: newHeight });
	}
};

/*********************************************************************************************
 * 🎠 SPLIDE MODULE
 */
const SplideManager = {
	/**
	 * Marge de pre-chargement du lazy-mount (convention thème, cf. mm_recommendations.js)
	 */
	observerRootMargin: '0px 0px 400px 0px',

	/**
	 * Initialize all Splide carousels with their data-splide configurations
	 *
	 * Les carrousels déjà visibles (ou proches du viewport) sont montés immédiatement ;
	 * les autres attendent d'approcher du viewport via IntersectionObserver. Sur les
	 * templates lourds (product : 6 carrousels, collection : 4) cela évite de monter
	 * toute la page en une seule long task au DOMContentLoaded.
	 */
	init() {
		const splideElements = window.getElements('.splide');
		if (!splideElements.length || !window.Splide) return;

		const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

		let initializedCount = 0;
		const sectionsWithSplide = [];

		// Sans IntersectionObserver, on retombe sur le montage immédiat de tout.
		const canObserve = typeof IntersectionObserver === 'function';
		let observer = null;

		if (canObserve) {
			observer = new IntersectionObserver((entries, obs) => {
				entries.forEach(entry => {
					if (!entry.isIntersecting) return;
					obs.unobserve(entry.target);
					const mounted = this._mountElement(entry.target, prefersReducedMotion, sectionsWithSplide);
					if (mounted) {
						window.emit('global:splide-initialized', {
							count: 1,
							sections: sectionsWithSplide
						});
					}
				});
			}, { rootMargin: this.observerRootMargin });
		}

		splideElements.forEach(element => {
			// Skip already initialized sliders
			if (element._splide) return;

			// Skip product media sliders - they're synced and initialized in mm-product-medias.liquid
			const isProductMediaSlider = element.hasAttribute('data-main-slider') ||
										element.hasAttribute('data-thumbnail-slider');
			if (isProductMediaSlider) return;

			// data-splide-eager force le montage immédiat (carrousel above-the-fold critique)
			const isEager = element.hasAttribute('data-splide-eager');

			if (observer && !isEager && !this._isNearViewport(element)) {
				observer.observe(element);
				return;
			}

			if (this._mountElement(element, prefersReducedMotion, sectionsWithSplide)) {
				initializedCount++;
			}
		});

		// Émettre un seul événement global avec la liste des sections
		if (initializedCount > 0) {
			window.emit('global:splide-initialized', {
				count: initializedCount,
				sections: sectionsWithSplide
			});
		}
	},

	/**
	 * Vrai si l'élément est déjà visible ou sur le point de l'être.
	 * Un carrousel masqué (drawer panier fermé, onglet inactif) a un rect nul :
	 * il part alors en lazy et sera monté quand il deviendra visible.
	 */
	_isNearViewport(element) {
		const rect = element.getBoundingClientRect();
		if (rect.width === 0 && rect.height === 0) return false;
		const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
		return rect.top < viewportHeight + 400 && rect.bottom > 0;
	},

	/**
	 * Met en pause l'autoplay d'un carrousel tout juste monté et ne le lance qu'une fois la page chargée
	 * et au repos, puis le met en pause dès qu'il sort du viewport (et le relance au retour).
	 */
	_deferAutoplay(element, splideInstance) {
		const autoplay = splideInstance.Components.Autoplay;
		if (!autoplay) return;
		autoplay.pause();

		const start = () => {
			if (typeof IntersectionObserver !== 'function') {
				autoplay.play();
				return;
			}
			new IntersectionObserver(entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) autoplay.play();
					else autoplay.pause();
				});
			}).observe(element);
		};
		const whenIdle = () => (window.requestIdleCallback ? requestIdleCallback(start, { timeout: 2000 }) : setTimeout(start, 200));

		if (document.readyState === 'complete') whenIdle();
		else window.addEventListener('load', whenIdle, { once: true });
	},

	/**
	 * Glissement horizontal au trackpad : un geste avance ou recule d'un slide. Le défilement vertical
	 * (molette, trackpad) reste celui de la page. Pause entre deux slides pour absorber l'inertie du geste.
	 */
	_bindHorizontalWheel(element, splideInstance) {
		const threshold = 30;
		const cooldown = 450;
		let travel = 0;
		let locked = false;

		element.addEventListener('wheel', (e) => {
			if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
			e.preventDefault();
			if (locked) return;

			travel += e.deltaX;
			if (Math.abs(travel) < threshold) return;

			splideInstance.go(travel > 0 ? '>' : '<');
			travel = 0;
			locked = true;
			setTimeout(() => { locked = false; }, cooldown);
		}, { passive: false });
	},

	/**
	 * Monte un carrousel unique. Retourne true si le montage a réussi.
	 * Extrait de init() pour être réutilisable par l'IntersectionObserver.
	 */
	_mountElement(element, prefersReducedMotion, sectionsWithSplide) {
		if (element._splide) return false;

		try {
			const configData = element.dataset.splide;
			let splideInstance;

			if (configData) {
				// Nettoyer et vérifier la validité du JSON
				const cleanedData = configData
					.replace(/'/g, '"')  // Remplacer les quotes simples par doubles
					.replace(/(\w+):/g, '"$1":');  // Ajouter des guillemets aux clés sans guillemets

				const config = JSON.parse(cleanedData);
				if (prefersReducedMotion) config.autoplay = false;
				splideInstance = new Splide(element, config).mount();
				// L'autoplay Splide tourne sur une boucle requestAnimationFrame : mis en pause dès le montage
				// (l'option 'pause' serait écrasée par l'attribut data-splide), il ne démarre qu'après
				// le chargement de la page et seulement à l'écran (voir _deferAutoplay).
				if (config.autoplay === true) this._deferAutoplay(element, splideInstance);
			} else {
				// Configuration par défaut si pas de data-splide
				const fallbackConfig = prefersReducedMotion ? { autoplay: false } : undefined;
				splideInstance = fallbackConfig
					? new Splide(element, fallbackConfig).mount()
					: new Splide(element).mount();
			}

			// Défilement horizontal au trackpad (ou molette inclinée), opt-in par attribut
			if (element.hasAttribute('data-splide-wheel-x') && splideInstance) {
				this._bindHorizontalWheel(element, splideInstance);
			}

			// Gérer la progress bar si elle existe
			const progressBar = element.querySelector('.splide__progress__bar');
			if (progressBar && splideInstance) {
				const updateProgress = function() {
					try {
						const end = splideInstance.Components.Controller.getEnd() + 1;
						const rate = Math.min((splideInstance.index + 1) / end, 1);
						progressBar.style.width = String(100 * rate) + '%';
					} catch (error) {
						window.log('error', 'SplideManager', 'Progress bar update failed');
					}
				};

				// Initialiser immédiatement et au montage
				splideInstance.on('mounted ready', updateProgress);
				splideInstance.on('move moved', updateProgress);

				// Force l'initialisation après un court délai
				requestAnimationFrame(() => {
					setTimeout(updateProgress, 50);
				});
			}

			// Masquer les arrows si tous les slides sont visibles (pas d'overflow)
			const arrowsEl = element.querySelector('.splide__arrows');
			if (arrowsEl) {
				const toggleArrows = () => {
					const overflow = splideInstance.Components.Layout.isOverflow();
					arrowsEl.style.visibility = overflow ? '' : 'hidden';
				};
				splideInstance.on('mounted resize', toggleArrows);
			}

			// Collecter les sections
			const sectionElement = element.closest('section');
			if (sectionElement) {
				const sectionName = sectionElement.className || 'unnamed-section';
				if (!sectionsWithSplide.includes(sectionName)) {
					sectionsWithSplide.push(sectionName);
				}
			}

			return true;

		} catch (error) {
			window.log('error', 'SplideManager', `JSON parsing failed for element: ${element.className}`);
			window.log('error', 'SplideManager', `Data-splide: ${element.dataset.splide}`);

			// Initialiser avec config par défaut en cas d'erreur
			try {
				const errorFallbackConfig = prefersReducedMotion ? { autoplay: false } : undefined;
				if (errorFallbackConfig) {
					new Splide(element, errorFallbackConfig).mount();
				} else {
					new Splide(element).mount();
				}
				return true;
			} catch (fallbackError) {
				window.log('error', 'SplideManager', `Fallback initialization failed: ${fallbackError.message}`);
				return false;
			}
		}
	}
};

/*********************************************************************************************
 * 📦 LOAD MORE MODULE
 */
const LoadMoreManager = {
	config: {
		selectors: {
			grid: '[data-load-more-grid]',
			btn: '[data-load-more-btn]',
			count: '[data-load-more-count]'
		}
	},

	/**
	 * Initialize a single load more instance within a section
	 */
	initSection(section) {
		const grid = section.querySelector(this.config.selectors.grid);
		const btn = section.querySelector(this.config.selectors.btn);
		const countEl = section.querySelector(this.config.selectors.count);
		if (!btn || !grid) return;

		const sectionId = section.dataset.sectionId;

		btn.addEventListener('click', async function() {
			const nextUrl = this.dataset.nextUrl;
			if (!nextUrl) return;

			this.disabled = true;
			this.style.opacity = '0.5';

			try {
				const separator = nextUrl.includes('?') ? '&' : '?';
				const fetchUrl = sectionId
					? nextUrl + separator + 'sections=' + sectionId
					: nextUrl;

				const res = await fetch(fetchUrl);
				if (!res.ok) throw new Error(res.status);

				let html;
				if (sectionId) {
					const json = await res.json();
					html = json[sectionId];
				} else {
					html = await res.text();
				}

				const parser = new DOMParser();
				const doc = parser.parseFromString(html, 'text/html');

				/* Append new items */
				const gridSelector = grid.dataset.loadMoreGrid || '[data-load-more-grid]';
				const newItems = doc.querySelectorAll(gridSelector + ' > *');
				if (newItems.length === 0) throw new Error('No items found');
				newItems.forEach(function(item) { grid.appendChild(item); });

				/* Update next URL or hide button */
				const newBtn = doc.querySelector('[data-load-more-btn]');
				/* Lien crawlable (ClickUp 86cbbcvjd) : le garder aligne sur le bouton,
				   sinon il continue de pointer vers une page deja chargee. */
				const nextLink = section.querySelector('[data-load-more-next-link]');
				if (newBtn && newBtn.dataset.nextUrl) {
					this.dataset.nextUrl = newBtn.dataset.nextUrl;
					if (nextLink) nextLink.href = newBtn.dataset.nextUrl;
					this.disabled = false;
					this.style.opacity = '1';
				} else {
					this.classList.add('mm-hidden');
					if (nextLink) nextLink.remove();
				}

				/* Update counter */
				const newCount = doc.querySelector('[data-load-more-count]');
				if (newCount && countEl) {
					countEl.textContent = newCount.textContent.trim();
					countEl.dataset.current = newCount.dataset.current;
				}

				window.emit('global:load-more-loaded', {
					sectionId: sectionId,
					newItemsCount: newItems.length
				});
			} catch (e) {
				console.error('Load more error:', e);
				this.disabled = false;
				this.style.opacity = '1';
			}
		});
	},

	/**
	 * Initialize all load more instances on the page
	 */
	init() {
		const sections = document.querySelectorAll('[data-section-id]');
		sections.forEach(section => {
			if (section.querySelector(this.config.selectors.btn)) {
				this.initSection(section);
			}
		});
	}
};

/*********************************************************************************************
 * 🔒 FOCUS TRAP MANAGER
 * Shared focus trap helper for dialogs/overlays (mobile menu & cart drawer keep their own).
 * Activates keyboard trap (Tab/Shift+Tab), handles Escape, restores focus on deactivate.
 */
const FocusTrapManager = {
	config: {
		focusableSelector: 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
	},

	activeTraps: new Map(),

	/**
	 * Activate a focus trap inside container.
	 * @param {HTMLElement} container - The dialog element
	 * @param {Object} options
	 * @param {HTMLElement} [options.trigger] - Element to restore focus to on deactivate
	 * @param {Function} [options.onEscape] - Called when Escape is pressed (should close dialog)
	 * @param {boolean} [options.focusFirst=true] - Auto-focus first element on activate
	 * @param {HTMLElement} [options.initialFocus] - Element to focus instead of first (e.g. close button)
	 * @returns {string} trapId - Pass to deactivate()
	 */
	activate(container, { trigger = null, onEscape = null, focusFirst = true, initialFocus = null } = {}) {
		if (!container) return null;

		const focusableElements = container.querySelectorAll(this.config.focusableSelector);
		const visibleFocusable = Array.from(focusableElements).filter((el) => {
			return !el.closest('[hidden]') && !el.hasAttribute('inert') && el.offsetParent !== null;
		});

		const firstFocusable = visibleFocusable[0] || container;
		const lastFocusable = visibleFocusable[visibleFocusable.length - 1] || container;

		const trapId = `trap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
		const previouslyFocused = trigger || document.activeElement;

		const handleKeyDown = (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				if (typeof onEscape === 'function') {
					e.preventDefault();
					onEscape();
				}
				return;
			}

			if (e.key !== 'Tab') return;

			const focusables = Array.from(container.querySelectorAll(this.config.focusableSelector))
				.filter((el) => !el.closest('[hidden]') && !el.hasAttribute('inert') && el.offsetParent !== null);
			if (focusables.length === 0) {
				e.preventDefault();
				return;
			}

			const first = focusables[0];
			const last = focusables[focusables.length - 1];

			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		this.activeTraps.set(trapId, { container, handleKeyDown, previouslyFocused });

		if (focusFirst) {
			const target = initialFocus || firstFocusable;
			setTimeout(() => {
				try { target?.focus({ preventScroll: true }); } catch (e) { /* noop */ }
			}, 50);
		}

		window.log('info', '🔒 [FocusTrap]', `Activated ${trapId}`);
		return trapId;
	},

	/**
	 * Deactivate a focus trap and restore focus.
	 * @param {string} trapId
	 * @param {boolean} [restoreFocus=true]
	 */
	deactivate(trapId, restoreFocus = true) {
		if (!trapId) return;
		const trap = this.activeTraps.get(trapId);
		if (!trap) return;

		document.removeEventListener('keydown', trap.handleKeyDown);
		if (restoreFocus && trap.previouslyFocused && typeof trap.previouslyFocused.focus === 'function') {
			try { trap.previouslyFocused.focus({ preventScroll: true }); } catch (e) { /* noop */ }
		}
		this.activeTraps.delete(trapId);
		window.log('info', '🔒 [FocusTrap]', `Deactivated ${trapId}`);
	}
};

/*********************************************************************************************
 * 🎯 UI COMPONENTS MANAGER
 */
const UIComponentsManager = {
	/**
	 * Initialize all UI components
	 */
	init() {
		DropdownManager.init();
		ReadMoreManager.init();
		DragScrollManager.init();
		ExternalLinksManager.init();
		SplideManager.init();
		LoadMoreManager.init();
		DocumentHeightManager.update();
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		DocumentHeightManager.update();
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.LoaderManager = LoaderManager;
window.DropdownManager = DropdownManager;
window.ReadMoreManager = ReadMoreManager;
window.ExternalLinksManager = ExternalLinksManager;
window.SplideManager = SplideManager;
window.LoadMoreManager = LoadMoreManager;
window.DocumentHeightManager = DocumentHeightManager;
window.FocusTrapManager = FocusTrapManager;
window.UIComponentsManager = UIComponentsManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Active - Used in mm_cart.js
window.mmLoadThis = (elem) => LoaderManager.loadThis(elem);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	UIComponentsManager.init();
});

window.addEventListener('resize', () => {
	UIComponentsManager.onResize();
});